import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Smartphone, Download, Apple } from "lucide-react";

const DEFAULT_APP_INFO = {
  android_url: "/downloads/BBCconnect.apk",
  android_version: "",
  android_release_notes: "",
  android_enabled: true,

  ios_url: process.env.REACT_APP_APP_URL || "https://app.bbcbarani.org",
  ios_install_note:
    "Open in Safari, tap Share, then choose Add to Home Screen.",
};

export default function MobileApp() {
  const [appInfo, setAppInfo] = useState(DEFAULT_APP_INFO);

  useEffect(() => {
    api
      .get("/content/mobile_app")
      .then((r) => {
        setAppInfo({
          ...DEFAULT_APP_INFO,
          ...(r.data || {}),
        });
      })
      .catch((e) => {
        console.warn(
          "Could not load mobile app settings. Using defaults.",
          e
        );
      });
  }, []);

  const androidUrl =
    appInfo.android_url || DEFAULT_APP_INFO.android_url;

  const androidVersion =
    appInfo.android_version || "";

  const androidNotes =
    appInfo.android_release_notes || "";

  const androidEnabled =
    appInfo.android_enabled !== false;

  const iosUrl =
    process.env.REACT_APP_APP_URL || appInfo.ios_url || DEFAULT_APP_INFO.ios_url;

  const iosNote =
    appInfo.ios_install_note ||
    DEFAULT_APP_INFO.ios_install_note;

  return (
    <section
      id="mobile-app"
      className="py-24 bg-[#F3EFEA]"
      data-testid="mobile-app-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        <div className="text-center mb-16">
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#1C201E]">
            Stay Connected with BBC Connect
          </h2>

          <p className="mt-4 text-[#5C6661] max-w-2xl mx-auto">
            Access Bible reading, daily devotionals,
            announcements, prayer requests, church messages,
            Bible quizzes, and more through BBC Connect.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

          {/* Android Card */}
          <div className="bg-white p-8 rounded-3xl border border-[#E5E0D8] shadow-sm flex flex-col items-center text-center">

            <img
              src="/Android.webp"
              alt="Android"
              className="w-20 h-20 mb-6"
            />

            <h3 className="font-serif-display text-2xl text-[#1C201E] mb-2">
              Download for Android
            </h3>

            {androidVersion && (
              <p className="text-xs font-medium text-[#D4A373] uppercase tracking-widest mb-4">
                Version {androidVersion}
              </p>
            )}

            <p className="text-[#5C6661] text-sm mb-8 leading-relaxed">
              Install the BBC Connect app directly on your
              Android device using the APK file.
            </p>

            {androidEnabled && androidUrl ? (
              <a
                href={androidUrl}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#2C4C3B] text-white hover:bg-[#1A3326] transition-colors"
              >
                <Download className="w-4 h-4" />
                Download APK
              </a>
            ) : (
              <button
                disabled
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gray-200 text-gray-400 cursor-not-allowed"
              >
                Coming Soon
              </button>
            )}

            {androidNotes && (
              <div className="mt-6 p-4 bg-[#FAF9F6] rounded-xl text-left w-full">
                <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-wider mb-1">
                  Recent Updates
                </p>

                <p className="text-xs text-[#5C6661] whitespace-pre-line">
                  {androidNotes}
                </p>
              </div>
            )}
          </div>

          {/* iOS Card */}
          <div className="bg-white p-8 rounded-3xl border border-[#E5E0D8] shadow-sm flex flex-col items-center text-center">

            <img
              src="/IOS.webp"
              alt="iOS"
              className="w-20 h-20 mb-6"
            />

            <h3 className="font-serif-display text-2xl text-[#1C201E] mb-2">
              Install on iPhone / iPad
            </h3>

            <p className="text-xs font-medium text-[#D4A373] uppercase tracking-widest mb-4">
              Web App / PWA
            </p>

            <p className="text-[#5C6661] text-sm mb-8 leading-relaxed">
              Use BBC Connect as a high-performance web
              application on your iOS device.
            </p>

            <a
              href={iosUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-[#2C4C3B] text-[#2C4C3B] font-semibold hover:bg-[#2C4C3B] hover:text-white transition-all"
            >
              <Smartphone className="w-4 h-4" />
              Open BBC Connect
            </a>

            <div className="mt-6 flex flex-start gap-3 text-left w-full">

              <div className="p-2 bg-[#F3EFEA] rounded-lg">
                <Apple className="w-5 h-5 text-[#2C4C3B]" />
              </div>

              <div>
                <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-wider">
                  Instructions
                </p>

                <p className="text-xs text-[#5C6661] leading-relaxed">
                  {iosNote}
                </p>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
